import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { approvalTheme, transition } from '../theme';

interface ApprovalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

export const ApprovalButton: React.FC<ApprovalButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  className,
  disabled,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background';

  const variantStyles = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-ring shadow-md hover:shadow-lg',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-ring shadow-md hover:shadow-lg',
    success: 'accent-success bg-[var(--accent-bg-solid)] text-[var(--accent-text-on-solid)] hover:opacity-90 focus:ring-ring shadow-md hover:shadow-lg',
    danger: 'accent-destructive bg-[var(--accent-bg-solid)] text-[var(--accent-text-on-solid)] hover:opacity-90 focus:ring-ring shadow-md hover:shadow-lg',
    ghost: 'bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:ring-ring',
    outline: 'bg-transparent border-2 border-border text-foreground hover:border-muted-foreground hover:bg-accent focus:ring-ring',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2.5 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5',
  };

  const disabledStyles = 'opacity-50 cursor-not-allowed hover:transform-none';

  return (
    <button
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        (disabled || loading) && disabledStyles,
        className
      )}
      disabled={disabled || loading}
      style={{
        transition: transition(['all']),
      }}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {!loading && icon && iconPosition === 'left' && icon}
      {children}
      {!loading && icon && iconPosition === 'right' && icon}
    </button>
  );
};

interface ApprovalIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  label?: string;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const ApprovalIconButton: React.FC<ApprovalIconButtonProps> = ({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  loading = false,
  className,
  disabled,
  children,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background';

  const variantStyles = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-ring',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-ring',
    success: 'accent-success bg-[var(--accent-bg-solid)] text-[var(--accent-text-on-solid)] hover:opacity-90 focus:ring-ring',
    danger: 'accent-destructive bg-[var(--accent-bg-solid)] text-[var(--accent-text-on-solid)] hover:opacity-90 focus:ring-ring',
    ghost: 'bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:ring-ring',
    outline: 'bg-transparent border-2 border-border text-foreground hover:border-muted-foreground hover:bg-accent focus:ring-ring',
  };

  const sizeStyles = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
  };

  const disabledStyles = 'opacity-50 cursor-not-allowed';

  return (
    <button
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        (disabled || loading) && disabledStyles,
        className
      )}
      disabled={disabled || loading}
      aria-label={label}
      title={label}
      style={{
        transition: transition(['all']),
      }}
      {...props}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (icon || children)}
    </button>
  );
};
